import { createSelector } from "@reduxjs/toolkit";
import type { ChartOfAccounts, ChartOfAccountsItem } from "../../../../../../api/types/svTypes.ts";
import { AccountType, ReportType } from "../../../../../../api/types/svTypes.ts";
import { isIrrMeasureMeta } from "../../components/builder/common/utilities/metaUtils.ts";
import { isStatementLineMeasure } from "../../components/builder/financialStatement/utils/statementLines.ts";
import { createFieldCaptionLookup } from "../../components/viewer/financialStatement/statementGridModel.ts";
import { dataSetMetaDataEmpty } from "../slices/metaDataSlice";
import type { RootState } from "../types/store.types";
import { filterDimensions } from "../utils/filterDimensionsByReportType";
import { filterDimensionsStructureByReportType } from "../utils/filterDimensionsStructureByReportType";

export const selectMetaDataLoaded = (state: RootState) => state.metaData.loaded;
export const selectMetaDataLoadingFailed = (state: RootState) => state.metaData.loadingFailed;
export const selectCurrentDataSetId = (state: RootState) => state.metaData.dataSetId;
const selectCurrentDataSet = (state: RootState) =>
  state.metaData.dataSets.find((d) => d.dataSetId === state.metaData.dataSetId) ?? dataSetMetaDataEmpty;
export const selectDimensions = createSelector(selectCurrentDataSet, (state) => state.dimensions);
export const selectDimensionsStructure = createSelector(selectCurrentDataSet, (state) => state.dimensionsStructure);
export const selectDimensionsStructureForCurrentReportType = createSelector(
  selectDimensionsStructure,
  (state: RootState) => state.fieldsState.fieldsStateReducer.reportType,
  (structure, reportType) => filterDimensionsStructureByReportType(structure, reportType)
);
export const selectMeasures = createSelector(selectCurrentDataSet, (state) => state.measures);
export const selectAllMeasures = createSelector(selectCurrentDataSet, (state) => state.allMeasures);
export const selectFieldCaptionLookup = createSelector(selectMeasures, selectDimensions, (measures, dimensions) =>
  createFieldCaptionLookup([...measures, ...dimensions])
);
export const selectMeasuresForCurrentReportType = createSelector(
  selectMeasures,
  (state: RootState) => state.metaData.reportType,
  (measures, reportType) =>
    reportType === ReportType.FinancialStatement ? measures.filter(isStatementLineMeasure) : measures
);
export const selectMeasuresStructure = createSelector(selectCurrentDataSet, (state) => state.measuresStructure);
export const selectMetrics = createSelector(selectCurrentDataSet, (state) => state.metrics);
export const selectMetricsStructure = createSelector(selectCurrentDataSet, (state) => state.metricsStructure);
export const selectAggregations = createSelector(selectCurrentDataSet, (state) => state.aggregations);
export const selectAvailableMeasureFields = createSelector(
  selectMeasuresForCurrentReportType,
  selectAggregations,
  (state: RootState) => state.fieldsState.fieldsStateReducer.reportType,
  (measures, aggregations, reportType) => {
    if (reportType === ReportType.CashFlow) {
      return measures.filter(isIrrMeasureMeta);
    }
    if (reportType === ReportType.FinancialStatement) {
      return measures;
    }
    return measures.length === 0 ? [] : [...measures, ...aggregations];
  }
);
export const selectFunctions = createSelector(selectCurrentDataSet, (state) => state.functions);
export const selectMeasureGroups = createSelector(selectCurrentDataSet, (state) => state.measureGroups);
export const selectMeasureSubgroups = createSelector(selectCurrentDataSet, (state) => state.measureSubgroups);
export const selectChartOfAccountsState = (state: RootState) => state.metaData.chartOfAccounts;
export const selectDimensionsForCurrentReportType = createSelector(
  selectDimensions,
  (state: RootState) => state.metaData.reportType,
  (state: RootState) => state.fieldsState.listFieldsReducer.ledger,
  (dimensions, reportType, ledger) => filterDimensions(reportType, dimensions, ledger)
);

export const isPostingAccount = (account: ChartOfAccountsItem): boolean => account.accountType === AccountType.Posting;

export const selectPostingChartOfAccounts = createSelector(selectChartOfAccountsState, (chartOfAccounts) => {
  if (chartOfAccounts) {
    return {
      gl: chartOfAccounts.gl?.filter(isPostingAccount) || [],
      memo: chartOfAccounts.memo?.filter(isPostingAccount) || [],
    } as ChartOfAccounts;
  }
  return undefined;
});

export const selectChartOfAccounts = createSelector(selectChartOfAccountsState, (chartOfAccounts) => {
  if (chartOfAccounts) {
    return {
      gl: chartOfAccounts.gl || [],
      memo: chartOfAccounts.memo || [],
    } as ChartOfAccounts;
  }
  return undefined;
});

export const selectCurrencyDimensions = createSelector(selectCurrentDataSet, (state) => state.currencyDimensions);
export const selectCustomFormulaMeta = createSelector(selectCurrentDataSet, (state) => state.customFormulas);
export const selectLedgers = createSelector(selectCurrentDataSet, (state) => state.ledgers);
